import React, { useEffect, useState } from "react";
import { Box, Divider, Grid, Typography, Alert } from "@mui/material";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzSelectListPopupInput from "src/components/core-components/mui/inputs/ByzSelectListPopupInput";
import { useTranslate } from "src/locales";
import { referenceService } from "src/api/references/referenceService";
import { useAppSelector } from "src/app/hooks";
import { RootState } from "src/app/store";
import { WillOpenCardKey } from "src/api/request/requestService";

interface ProcessDirectionModalProps {
  open: boolean;
  onClose: () => void;
  serviceName?: string;
  onSave?: (directionLabel: string) => void;
  // Merkezi additional-info fetch için opsiyonel parametreler
  additionalInfoKey?: WillOpenCardKey;
  additionalInfoParams?: {
    serviceId: number;
    requestDetailId: number;
    ptId?: number;
    officeId?: number;
  };
}

const ProcessDirectionModal: React.FC<ProcessDirectionModalProps> = ({
  open,
  onClose,
  serviceName = "Seçilen Hizmet Adı",
  onSave,
  additionalInfoKey,
  additionalInfoParams,
}) => {
  const { t } = useTranslate();
  const [selectedDirection, setSelectedDirection] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const { selectedOfficeId } = useAppSelector((state: RootState) => ({
    selectedOfficeId: state.auth.selectedOfficeId,
  }));
  const [additionalInfo, setAdditionalInfo] = useState<any>(null);

  // Modal açıldığında seçimleri sıfırla
  React.useEffect(() => {
    if (open) {
      setSelectedDirection("");
      setError("");
    }
  }, [open]);

  // WillOpenCard KEY bazlı ek bilgi formunu merkezi servisten yükle
  useEffect(() => {
    if (!open) return;

    const loadAdditionalInfo = async () => {
      try {
        // Backend sözleşmesine göre form tipi sabit PROCESS_DIRECTION
        const res = await referenceService.additionalInfo.getFields({
          formtype: "PROCESS_DIRECTION",
          offid:
            additionalInfoParams?.officeId ??
            (selectedOfficeId ? Number(selectedOfficeId) : undefined),
        });
        if (res) {
          setAdditionalInfo(res);
        }

        const field = res.fields && res.fields.length > 0 ? res.fields[0] : null;
        if (field && Array.isArray(field.options)) {
          const mapped = field.options.map((opt) => ({
            value: String(opt.value),
            label: opt.label ?? "",
          }));
          setOptions(mapped);
        } else {
          // Fallback: sabit yön listesi
          setOptions([
            { value: "1", label: "SAĞ" },
            { value: "2", label: "SOL" },
            { value: "3", label: "AYRIM YOK" },
          ]);
        }
      } catch {
        // Hata durumunda mevcut manuel akış devam etsin; sabit listeye düş
        setOptions([
          { value: "1", label: "SAĞ" },
          { value: "2", label: "SOL" },
          { value: "3", label: "AYRIM YOK" },
        ]);
      }
    };

    loadAdditionalInfo();
  }, [open, additionalInfoParams, selectedOfficeId]);

  const handleSave = () => {
    if (!selectedDirection) {
      setError(t("patient-request-service.direction-options.validation") || "İşlem yönü seçiniz.");
      return;
    }
    setError("");
    onSave?.(selectedDirection);
    if (!onSave) {
    onClose();
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "center",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {t("patient-request.process-direction.title", {
            service: serviceName,
          } as any)}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} disabled={!selectedDirection} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t("patient-request.process-direction.direction")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={8}>
            <ByzSelectListPopupInput
              fullWidth
              size="small"
              label={t("patient-request.process-direction.direction")}
              value={
                options.find((opt) => opt.value === selectedDirection)?.label ||
                ""
              }
              selectedId={selectedDirection || undefined}
              dialogMaxHeight="60vh"
              staticData={options.map((opt) => ({
                id: opt.value,
                NAME: opt.label,
              }))}
              displayField="NAME"
              onChange={(rows) => {
                const list = Array.isArray(rows) ? rows : (rows ? [rows] : []);
                const first = list[0];
                if (first) {
                  setSelectedDirection(String(first.id));
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 3 }} />
      </Box>
    </OpenComponentDialog>
  );
};

export default ProcessDirectionModal;

